<template>
  <div :class="[$style.config, $style.wrap]" data-special-features-v="1.2.0">
    <WithBackdrop :backdrop="computedBackdrop" :class="$style.backdrop">
      <BaseContainer :max-width="1260">
        <BaseHeading
          v-if="computedHeader"
          :as="computedHeaderWeight"
          size="h2"
          :visually-hidden="computedVisuallyHideHeader"
        >
          {{ computedHeader }}
        </BaseHeading>

        <div v-if="computedActiveSlide" :class="$style.slide">
          <Transition name="fade" mode="out-in">
            <SpecialFeaturesSlide
              :key="computedActiveSlide.sys.id"
              :data="computedActiveSlide"
            />
          </Transition>
        </div>

        <!-- Mobile: Stack -->
        <div
          v-if="computedSlides.length"
          v-show="!computedIsDesktop"
          :class="[
            $style.mobileTiles,
            computedSlides.length % 2 !== 0 && $style.odd,
          ]"
        >
          <SpecialFeaturesTile
            v-for="(slide, index) in computedSlides"
            :key="slide.sys.id"
            :data="slide"
            :active-slide="computedActiveSlide"
            @click="handleTileClick(index)"
          />
        </div>

        <!-- Desktop: Slider -->
        <div v-show="computedIsDesktop" :class="$style.desktopTiles">
          <div
            v-if="computedSlides.length"
            ref="desktopTiles"
            :class="$style.desktopTilesSwiper"
            class="swiper"
          >
            <div class="swiper-wrapper">
              <div
                v-for="(slide, index) in computedSlides"
                :key="slide.sys.id"
                class="swiper-slide"
              >
                <SpecialFeaturesTile
                  :data="slide"
                  :active-slide="computedActiveSlide"
                  @click="handleTileClick(index)"
                />
              </div>
            </div>
          </div>

          <div
            v-if="showCarouselPaging"
            :class="[$style.prev, $style.carouselArrow]"
          >
            <CarouselArrow
              :disabled="!computedShowPrev"
              direction="slidePrev"
              @click="handlePrev"
            />
          </div>
          <div
            v-if="showCarouselPaging"
            :class="[$style.next, $style.carouselArrow]"
          >
            <CarouselArrow
              :disabled="!computedShowNext"
              direction="slideNext"
              @click="handleNext"
            />
          </div>
        </div>
      </BaseContainer>
    </WithBackdrop>
  </div>
</template>

<script setup lang="ts">
import type { Swiper as SwiperType } from 'swiper'
import Swiper from 'swiper'
import 'swiper/css'

import type { TypeSpecialFeaturesWithoutUnresolvableLinksResponse } from '~/types/contentful'

// No idea why but Swiper's types don't include size or virtual size.
type SwiperTypeWithSizes = SwiperType & { size: number; virtualSize: number }

const siteMeasurementStore = useSiteMeasurementStore()
const activeSlideIndex = ref(0)
const showCarouselPaging = ref(false)

const desktopTiles = ref<(HTMLDivElement & { swiper: SwiperType }) | null>(null)

const props = defineProps<{
  data: TypeSpecialFeaturesWithoutUnresolvableLinksResponse
}>()

const computedBackdrop = computed(() => props?.data?.fields?.backgroundImage)
const computedHeaderWeight = computed(() => props?.data?.fields?.headerWeight)
const computedHeader = computed(() => props?.data?.fields?.header)
const computedVisuallyHideHeader = computed(
  () => props?.data?.fields?.visuallyHideHeader,
)
const computedSlides = computed(
  () =>
    props?.data?.fields?.slides?.filter(instanceOfSpecialFeaturesSlide) || [],
)
const computedActiveSlide = computed(
  () => computedSlides.value?.[activeSlideIndex.value],
)
const computedIsDesktop = computed(() => {
  return siteMeasurementStore?.vWidth >= 1024
})

const swiperInstance = ref<null | SwiperTypeWithSizes>(null)

const handleTileClick = (index: number) => {
  activeSlideIndex.value = index
}

const handlePrev = () => {
  swiperInstance.value?.slidePrev?.()
}

const handleNext = () => {
  swiperInstance.value?.slideNext?.()
}

const computedShowPrev = computed(() => {
  return !swiperInstance.value?.isBeginning
})

const computedShowNext = computed(() => {
  return !swiperInstance.value?.isEnd
})

onMounted(() => {
  if (desktopTiles.value) {
    swiperInstance.value = new Swiper(desktopTiles.value, {
      slidesPerView: 1.2,
      // centeredSlides: true,
      centerInsufficientSlides: true,
      spaceBetween: 15,
      breakpoints: {
        576: {
          slidesPerView: 2,
          spaceBetween: 15,
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 10,
        },
      },
      on: {
        resize: () => {
          if (swiperInstance.value?.virtualSize && swiperInstance.value?.size) {
            showCarouselPaging.value =
              swiperInstance.value?.virtualSize > swiperInstance.value?.size
          }
        },
      },
    }) as SwiperTypeWithSizes
  }
})
</script>

<style lang="scss" module>
.config {
  --special-features-accent-color: var(--purple-light);
  --special-features-active-color: var(--purple-dark);

  @include breakpoint-variables(
    '--special-features-slide-margin',
    (
      base: 0 0 #{to-rem(30px)},
      sm: 0 0 #{to-rem(48px)},
      md: 0 0 #{to-rem(48px)},
      lg: 0 0 #{to-rem(48px)},
    )
  );

  @include breakpoint-variables(
    '--special-features-backdrop-padding',
    (
      base: #{to-rem(36px)} 0 #{to-rem(30px)},
      sm: #{to-rem(36px)} 0 #{to-rem(30px)},
      md: #{to-rem(78px)} 0 #{to-rem(78px)},
      lg: #{to-rem(78px)} 0 #{to-rem(78px)},
    )
  );

  @include breakpoint-variables(
    '--special-features-mobile-tiles-grid-template-columns',
    (
      base: 1fr,
      sm: 1fr 1fr,
      md: 1fr 1fr,
      lg: 1fr 1fr,
    )
  );

  :global(.swiper-slide) {
    height: auto;
  }
}

.backdrop {
  padding: var(
    --special-features-backdrop-padding,
    to-rem(36px) 0 to-rem(30px)
  );
}

.slide {
  margin: var(--special-features-slide-margin, 0 0 to-rem(30px));

  :global(.fade-enter),
  :global(.fade-leave-to) {
    opacity: 0;
  }
}

.mobileTiles {
  display: grid;
  gap: var(--special-features-mobile-tiles-grid-gap, 9px 9px);
  grid-template-columns: var(
    --special-features-mobile-tiles-grid-template-columns,
    1fr
  );

  &.odd {
    @include media('sm') {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
    }
  }
}

.desktopTiles {
  position: relative;

  .carouselArrow {
    display: none;

    @include media('md') {
      display: block;
      padding: 10px;

      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;

      &.prev {
        left: 0;
      }

      &.next {
        right: 0;
      }
    }

    @include media('lg') {
      &.prev {
        left: auto;
        right: 100%;
      }

      &.next {
        right: auto;
        left: 100%;
      }
    }
  }
}

.desktopTilesSwiper {
  --swiper-overflow: hidden;
}
</style>
